/**
 * ledger-groups.js — the Chart of Accounts skeleton.
 *
 * WHY THIS EXISTS
 * Until now the nominal heads of this application were hardcoded enums:
 * `INCOME_CATEGORIES` and `EXPENSE_CATEGORIES` in lib/accounts, plus five
 * journal-only heads in lib/journal. Adding "Electricity — Godown" was a code
 * change and a deploy. Worse, those heads had no accounting classification at
 * all, so nothing could say whether a figure belonged above or below the gross
 * profit line, and a balance sheet had to be constructed with owner's equity as
 * a plug rather than derived.
 *
 * A GROUP is the classification. A LEDGER is the account itself, and every
 * ledger sits under exactly one group. That is the whole model, and it is the
 * one Tally uses, because it is the one Indian accountants already know.
 *
 * WHAT IS IN HERE AND WHAT IS NOT
 * This module is PURE and holds the PRIMARY groups — the fifteen-odd heads that
 * every set of books has and that reports reason about by name. A business's
 * own sub-groups ("Vehicle Loans" under Loans & Liabilities) are DOCUMENTS in
 * the `Groups` collection, created at will. The primaries are seeded as
 * documents too, so that the tree is uniform, but they are defined here because
 * the balance sheet has to know that `bank_accounts` is where the bank balance
 * lives and cannot discover that by reading a name.
 *
 * NATURE IS THE ONE FIELD THAT MATTERS
 * Four values, and every report in the application is a consequence of them:
 *
 *   ASSET      debit balance is normal. Balance sheet, left side.
 *   LIABILITY  credit balance is normal. Balance sheet, right side.
 *   INCOME     credit balance is normal. Profit and loss.
 *   EXPENSE    debit balance is normal. Profit and loss.
 *
 * A group cannot change its nature after ledgers hang off it, because the
 * figures already posted were classified under the old one.
 */

/* ------------------------------------------------------------------ *
 * Nature
 * ------------------------------------------------------------------ */

export const NATURES = {
  ASSET: 'asset',
  LIABILITY: 'liability',
  INCOME: 'income',
  EXPENSE: 'expense',
};

export const NATURE_CONFIG = {
  [NATURES.ASSET]: {
    value: NATURES.ASSET,
    label: 'Asset',
    /** The side on which a balance of this nature normally sits. */
    normalSide: 'debit',
    statement: 'balance-sheet',
    /** Which half of the balance sheet. */
    sheetSide: 'assets',
  },
  [NATURES.LIABILITY]: {
    value: NATURES.LIABILITY,
    label: 'Liability',
    normalSide: 'credit',
    statement: 'balance-sheet',
    sheetSide: 'liabilities',
  },
  [NATURES.INCOME]: {
    value: NATURES.INCOME,
    label: 'Income',
    normalSide: 'credit',
    statement: 'profit-and-loss',
    sheetSide: null,
  },
  [NATURES.EXPENSE]: {
    value: NATURES.EXPENSE,
    label: 'Expense',
    normalSide: 'debit',
    statement: 'profit-and-loss',
    sheetSide: null,
  },
};

export const NATURE_OPTIONS = Object.values(NATURE_CONFIG).map((n) => ({
  value: n.value,
  label: n.label,
}));

export function natureConfig(nature) {
  return NATURE_CONFIG[nature] || NATURE_CONFIG[NATURES.ASSET];
}


/**
 * +1 when a DEBIT increases a balance of this nature, -1 when a debit reduces
 * it.
 *
 * Every signed balance in this application is stored as "positive means debit",
 * so this is only needed when a figure has to be PRESENTED the way an
 * accountant expects — sales shown as a positive number in the profit and loss
 * even though its ledger balance is a credit and therefore negative.
 */
export function naturalSign(nature) {
  return natureConfig(nature).normalSide === 'debit' ? 1 : -1;
}

/* ------------------------------------------------------------------ *
 * Behavioural flags
 *
 * A group is not only a classification. A handful of them carry BEHAVIOUR that
 * screens and reports depend on, and the behaviour has to be attached to the
 * group rather than guessed from its name — a business that renames "Cash-in-
 * Hand" to "Till" must not lose its cash book.
 *
 * Flags are INHERITED down the tree. A sub-group of Bank Accounts is a bank
 * group; `resolveFlags` below walks the parents.
 * ------------------------------------------------------------------ */

export const GROUP_FLAGS = {
  /** Money that can be paid and received: cash, bank, card, wallet. Contra
   *  vouchers may only touch these, and the Cash/Bank Book reads them. */
  CASH: 'isCash',
  /** Specifically a bank, which has a Bank Book and a reconciliation. */
  BANK: 'isBank',
  /** Sundry Debtors / Sundry Creditors. A ledger here IS a party: it carries
   *  bill-wise references, a credit period and an ageing. */
  PARTY: 'isParty',
  /** Duties & Taxes. The GST reports read these rather than a hardcoded name. */
  TAX: 'isTax',
  /** Stock-in-Hand: valued from the inventory module, not from postings. */
  STOCK: 'isStock',
  /** Above the gross-profit line: sales, purchases, direct costs, stock. */
  TRADING: 'isTrading',
  /** Capital and reserves — the owner's stake, not a trading liability. */
  CAPITAL: 'isCapital',
};

/* ------------------------------------------------------------------ *
 * The primary groups
 *
 * `id` is the DOCUMENT ID the seed writes, and it is stable for the life of the
 * business: reports match on it, so renaming a group is safe and re-seeding is
 * idempotent. `sortOrder` is the order a balance sheet expects, not
 * alphabetical — an accountant reads Capital, then Loans, then Current
 * Liabilities, and a re-ordered statement reads as a different statement.
 * ------------------------------------------------------------------ */

export const PRIMARY_GROUPS = [
  /* --- Liabilities ------------------------------------------------- */
  {
    id: 'capital_account',
    name: 'Capital Account',
    nature: NATURES.LIABILITY,
    parentId: null,
    sortOrder: 100,
    flags: [GROUP_FLAGS.CAPITAL],
    description: "The owner's stake: capital introduced, drawings, reserves.",
  },
  {
    id: 'reserves_surplus',
    name: 'Reserves & Surplus',
    nature: NATURES.LIABILITY,
    parentId: 'capital_account',
    sortOrder: 110,
    flags: [GROUP_FLAGS.CAPITAL],
    description: 'Profit retained in the business rather than drawn out.',
  },
  {
    id: 'loans_liabilities',
    name: 'Loans (Liability)',
    nature: NATURES.LIABILITY,
    parentId: null,
    sortOrder: 200,
    flags: [],
    description: 'Money borrowed and still owed.',
    aliases: ['Loans & Liabilities'],
  },
  {
    id: 'secured_loans',
    name: 'Secured Loans',
    nature: NATURES.LIABILITY,
    parentId: 'loans_liabilities',
    sortOrder: 210,
    flags: [],
    description: 'Borrowing against an asset — a vehicle loan, a mortgage.',
  },
  {
    id: 'unsecured_loans',
    name: 'Unsecured Loans',
    nature: NATURES.LIABILITY,
    parentId: 'loans_liabilities',
    sortOrder: 220,
    flags: [],
    description: 'Borrowing with nothing pledged — a hand loan from family.',
  },
  {
    id: 'bank_od',
    name: 'Bank OD Accounts',
    nature: NATURES.LIABILITY,
    parentId: 'loans_liabilities',
    sortOrder: 230,
    /**
     * An overdraft is a LIABILITY that is also spendable money, which is why it
     * carries both flags. A payment can be made from it and it appears in the
     * Bank Book, but it sits on the liabilities side of the balance sheet — and
     * adding it to the cash balance, which is what happens when it is filed
     * under Bank Accounts, overstates liquidity by the whole limit.
     */
    flags: [GROUP_FLAGS.CASH, GROUP_FLAGS.BANK],
    description: 'An overdraft or cash-credit account. Spendable, and owed.',
  },
  {
    id: 'current_liabilities',
    name: 'Current Liabilities',
    nature: NATURES.LIABILITY,
    parentId: null,
    sortOrder: 300,
    flags: [],
    description: 'What the business owes and expects to settle within the year.',
  },
  {
    id: 'sundry_creditors',
    name: 'Sundry Creditors',
    nature: NATURES.LIABILITY,
    parentId: 'current_liabilities',
    sortOrder: 310,
    flags: [GROUP_FLAGS.PARTY],
    description: 'Suppliers. One ledger per supplier, with bill-wise outstanding.',
  },
  {
    id: 'duties_taxes',
    name: 'Duties & Taxes',
    nature: NATURES.LIABILITY,
    parentId: 'current_liabilities',
    sortOrder: 320,
    flags: [GROUP_FLAGS.TAX],
    description: 'GST collected and paid, TDS, TCS — money held for the government.',
  },
  {
    id: 'provisions',
    name: 'Provisions',
    nature: NATURES.LIABILITY,
    parentId: 'current_liabilities',
    sortOrder: 330,
    flags: [],
    description: 'A cost incurred whose bill has not yet arrived.',
  },

  /* --- Assets ------------------------------------------------------- */
  {
    id: 'fixed_assets',
    name: 'Fixed Assets',
    nature: NATURES.ASSET,
    parentId: null,
    sortOrder: 400,
    flags: [],
    description: 'Things bought to be used rather than sold — a van, a shop fitting.',
  },
  {
    id: 'investments',
    name: 'Investments',
    nature: NATURES.ASSET,
    parentId: null,
    sortOrder: 450,
    flags: [],
    description: 'Money parked outside the business — shares, deposits, bonds.',
  },
  {
    id: 'current_assets',
    name: 'Current Assets',
    nature: NATURES.ASSET,
    parentId: null,
    sortOrder: 500,
    flags: [],
    description: 'What the business owns and expects to turn into cash this year.',
  },
  {
    id: 'bank_accounts',
    name: 'Bank Accounts',
    nature: NATURES.ASSET,
    parentId: 'current_assets',
    sortOrder: 510,
    flags: [GROUP_FLAGS.CASH, GROUP_FLAGS.BANK],
    description: 'Current and savings accounts. Each has its own Bank Book.',
  },
  {
    id: 'cash_in_hand',
    name: 'Cash-in-Hand',
    nature: NATURES.ASSET,
    parentId: 'current_assets',
    sortOrder: 520,
    flags: [GROUP_FLAGS.CASH],
    description: 'The till, the drawer, the petty-cash box.',
  },
  {
    id: 'sundry_debtors',
    name: 'Sundry Debtors',
    nature: NATURES.ASSET,
    parentId: 'current_assets',
    sortOrder: 530,
    flags: [GROUP_FLAGS.PARTY],
    description: 'Customers. One ledger per customer, with bill-wise outstanding.',
  },
  {
    id: 'stock_in_hand',
    name: 'Stock-in-Hand',
    nature: NATURES.ASSET,
    parentId: 'current_assets',
    sortOrder: 540,
    flags: [GROUP_FLAGS.STOCK, GROUP_FLAGS.TRADING],
    description: 'Goods on the shelf. Valued by the inventory module.',
  },
  {
    id: 'loans_advances_asset',
    name: 'Loans & Advances (Asset)',
    nature: NATURES.ASSET,
    parentId: 'current_assets',
    sortOrder: 550,
    flags: [],
    description: 'Money lent out or advanced, and expected back.',
  },
  {
    id: 'deposits_asset',
    name: 'Deposits (Asset)',
    nature: NATURES.ASSET,
    parentId: 'current_assets',
    sortOrder: 560,
    flags: [],
    description: 'Security deposits paid — rent, electricity, a distributorship.',
  },
  {
    id: 'misc_expenses_asset',
    name: 'Misc. Expenses (Asset)',
    nature: NATURES.ASSET,
    parentId: null,
    sortOrder: 600,
    flags: [],
    description: 'Preliminary expenses carried forward rather than written off at once.',
  },
  {
    id: 'suspense',
    name: 'Suspense A/c',
    nature: NATURES.ASSET,
    parentId: null,
    sortOrder: 650,
    flags: [],
    description:
      'Where an amount waits while somebody works out what it was. A set of books should end the year with nothing in here.',
  },

  /* --- Trading and profit & loss ------------------------------------ */
  {
    id: 'sales_accounts',
    name: 'Sales Accounts',
    nature: NATURES.INCOME,
    parentId: null,
    sortOrder: 700,
    flags: [GROUP_FLAGS.TRADING],
    description: 'Revenue from what the business sells.',
  },
  {
    id: 'purchase_accounts',
    name: 'Purchase Accounts',
    nature: NATURES.EXPENSE,
    parentId: null,
    sortOrder: 710,
    flags: [GROUP_FLAGS.TRADING],
    description: 'The cost of the goods bought to sell.',
  },
  {
    id: 'direct_expenses',
    name: 'Direct Expenses',
    nature: NATURES.EXPENSE,
    parentId: null,
    sortOrder: 720,
    flags: [GROUP_FLAGS.TRADING],
    description: 'Costs of getting goods ready to sell — freight inward, wages, packing.',
  },
  {
    id: 'direct_income',
    name: 'Direct Income',
    nature: NATURES.INCOME,
    parentId: null,
    sortOrder: 730,
    flags: [GROUP_FLAGS.TRADING],
    description: 'Trading income other than sales — job work, service charges.',
    aliases: ['Direct Incomes'],
  },
  {
    id: 'indirect_expenses',
    name: 'Indirect Expenses',
    nature: NATURES.EXPENSE,
    parentId: null,
    sortOrder: 740,
    flags: [],
    description: 'The cost of running the business — rent, salaries, electricity.',
  },
  {
    id: 'indirect_income',
    name: 'Indirect Income',
    nature: NATURES.INCOME,
    parentId: null,
    sortOrder: 750,
    flags: [],
    description: 'Income from outside the trade — interest, commission, rent received.',
    aliases: ['Indirect Incomes'],
  },
];


const PRIMARY_INDEX = new Map(PRIMARY_GROUPS.map((g) => [g.id, g]));


/** True for a group the seed created, which may be renamed but not deleted. */
export const isPrimaryGroup = (id) => PRIMARY_INDEX.has(id);

/* Handy named ids, so no screen has to spell a string that a typo would
   silently turn into "no match". */
export const GROUP_IDS = {
  CAPITAL: 'capital_account',
  RESERVES: 'reserves_surplus',
  LOANS: 'loans_liabilities',
  BANK_OD: 'bank_od',
  CURRENT_LIABILITIES: 'current_liabilities',
  SUNDRY_CREDITORS: 'sundry_creditors',
  DUTIES_TAXES: 'duties_taxes',
  PROVISIONS: 'provisions',
  FIXED_ASSETS: 'fixed_assets',
  INVESTMENTS: 'investments',
  CURRENT_ASSETS: 'current_assets',
  BANK: 'bank_accounts',
  CASH: 'cash_in_hand',
  SUNDRY_DEBTORS: 'sundry_debtors',
  STOCK: 'stock_in_hand',
  LOANS_ADVANCES: 'loans_advances_asset',
  DEPOSITS: 'deposits_asset',
  SUSPENSE: 'suspense',
  SALES: 'sales_accounts',
  PURCHASES: 'purchase_accounts',
  DIRECT_EXPENSES: 'direct_expenses',
  DIRECT_INCOME: 'direct_income',
  INDIRECT_EXPENSES: 'indirect_expenses',
  INDIRECT_INCOME: 'indirect_income',
};

/* ------------------------------------------------------------------ *
 * Working with a loaded tree
 *
 * Everything below takes the `Groups` collection as it was read from Firestore —
 * primaries and the business's own sub-groups together — because that is what a
 * screen actually holds. Nothing here reads the database.
 * ------------------------------------------------------------------ */

/** Index a group list by id, for O(1) parent walks. */
export function indexGroups(groups = []) {
  return new Map(groups.map((g) => [g.id, g]));
}

/**
 * Walk a group up to its primary, returning [self, parent, …, primary].
 *
 * Cycle-guarded: a corrupted parent pointer must produce a short chain, not a
 * hung tab. Twenty is far beyond any real chart of accounts.
 */
export function ancestryOf(groupId, index) {
  const chain = [];
  let current = index.get(groupId);
  let guard = 0;
  while (current && guard < 20) {
    chain.push(current);
    if (!current.parentId) break;
    current = index.get(current.parentId);
    guard += 1;
  }
  return chain;
}


/**
 * The flags that apply to a group, including those inherited from its parents.
 *
 * Inheritance is what lets a business create "HDFC — Current" under Bank
 * Accounts and have it behave as a bank without re-declaring anything.
 */
export function resolveFlags(groupId, index) {
  const chain = ancestryOf(groupId, index);
  const flags = new Set();
  chain.forEach((group) => {
    (group.flags || []).forEach((flag) => flags.add(flag));
  });
  return flags;
}


/**
 * A group's nature, resolved through its parents.
 *
 * A sub-group SHOULD carry its own copy — the seed and the create form both
 * write one — but a group whose nature is missing inherits rather than
 * defaulting to Asset, because a mis-classified expense is a wrong profit
 * figure and a missing field should not produce one silently.
 */
export function natureOf(groupId, index) {
  const chain = ancestryOf(groupId, index);
  const found = chain.find((g) => g.nature);
  return found ? found.nature : NATURES.ASSET;
}

/** "Current Assets › Bank Accounts › HDFC" — for a picker and a report row. */
export function groupPath(groupId, index, separator = ' › ') {
  return ancestryOf(groupId, index)
    .map((g) => g.name)
    .reverse()
    .join(separator);
}

/** Immediate children of a group, in display order. */
export function childrenOf(groupId, groups = []) {
  return groups
    .filter((g) => (g.parentId || null) === (groupId || null))
    .sort(sortGroups);
}

export function sortGroups(a, b) {
  const orderA = Number.isFinite(a?.sortOrder) ? a.sortOrder : 9000;
  const orderB = Number.isFinite(b?.sortOrder) ? b.sortOrder : 9000;
  if (orderA !== orderB) return orderA - orderB;
  return String(a?.name || '').localeCompare(String(b?.name || ''));
}

/**
 * Whether `candidateParentId` may be the parent of `groupId`.
 *
 * Refuses a cycle — making a group its own grandparent turns every tree walk
 * into an infinite loop and every report into a blank screen.
 */
export function canReparent(groupId, candidateParentId, index) {
  if (!candidateParentId) return true;
  if (candidateParentId === groupId) return false;
  return !ancestryOf(candidateParentId, index).some((g) => g.id === groupId);
}

/** Flat options for a "Under / Parent Group" picker, indented by depth. */
export function groupOptions(groups = [], { natureFilter = null, exclude = null } = {}) {
  const index = indexGroups(groups);
  const out = [];

  const walk = (parentId, depth) => {
    childrenOf(parentId, groups).forEach((group) => {
      if (exclude && (group.id === exclude || !canReparent(exclude, group.id, index))) return;
      const nature = natureOf(group.id, index);
      if (!natureFilter || nature === natureFilter) {
        out.push({
          value: group.id,
          label: `${'  '.repeat(depth)}${group.name}`,
          meta: groupPath(group.id, index),
          keywords: `${group.name} ${natureConfig(nature).label}`,
          nature,
          depth,
        });
      }
      walk(group.id, depth + 1);
    });
  };

  walk(null, 0);
  return out;
}

/* ------------------------------------------------------------------ *
 * Smart classification
 *
 * When somebody types "Electricity Expense" into the ledger picker and it does
 * not exist, the quick-create form opens with a group already chosen. It is a
 * SUGGESTION and the field stays editable — silently filing a ledger under a
 * guessed head is how a set of books ends up with rent in Direct Expenses and
 * nobody noticing until the gross margin looks wrong.
 * ------------------------------------------------------------------ */

const SUGGESTION_RULES = [
  { group: GROUP_IDS.BANK, words: ['bank', 'hdfc', 'icici', 'sbi', 'axis', 'kotak', 'union bank', 'canara', 'pnb', 'baroda', 'yes bank', 'idfc', 'indusind', 'current a/c', 'savings'] },
  { group: GROUP_IDS.CASH, words: ['cash', 'till', 'petty', 'drawer', 'counter cash'] },
  { group: GROUP_IDS.BANK_OD, words: ['overdraft', ' od ', 'cash credit', ' cc a/c'] },
  { group: GROUP_IDS.CAPITAL, words: ['capital', 'owner', 'proprietor', 'partner', 'drawings', 'equity'] },
  { group: GROUP_IDS.DUTIES_TAXES, words: ['gst', 'cgst', 'sgst', 'igst', 'cess', 'tds', 'tcs', 'input tax', 'output tax', 'duty', 'vat'] },
  { group: GROUP_IDS.SALES, words: ['sales', 'revenue', 'turnover'] },
  { group: GROUP_IDS.PURCHASES, words: ['purchase', 'goods bought'] },
  { group: GROUP_IDS.LOANS, words: ['loan', 'emi', 'borrow', 'finance ltd', 'hire purchase'] },
  { group: GROUP_IDS.FIXED_ASSETS, words: ['machinery', 'furniture', 'vehicle', 'van', 'computer', 'laptop', 'building', 'land', 'equipment', 'fixture', 'plant', 'air conditioner'] },
  { group: GROUP_IDS.DIRECT_EXPENSES, words: ['freight inward', 'carriage inward', 'wages', 'packing', 'labour', 'job work', 'octroi', 'loading', 'unloading'] },
  { group: GROUP_IDS.INDIRECT_INCOME, words: ['interest received', 'commission received', 'rent received', 'discount received', 'scrap', 'other income'] },
  { group: GROUP_IDS.INDIRECT_EXPENSES, words: ['rent', 'salary', 'salaries', 'electricity', 'telephone', 'internet', 'repair', 'insurance', 'marketing', 'advertis', 'professional', 'audit', 'bank charge', 'interest paid', 'stationery', 'travel', 'conveyance', 'postage', 'fuel', 'depreciation', 'expense', 'charges', 'fee'] },
  { group: GROUP_IDS.SUNDRY_DEBTORS, words: ['customer', 'debtor', 'buyer'] },
  { group: GROUP_IDS.SUNDRY_CREDITORS, words: ['supplier', 'creditor', 'vendor'] },
];

/**
 * Suggest a group for a ledger name.
 *
 * @returns {{ groupId: string|null, confidence: 'high'|'low'|'none' }}
 *
 * The confidence is returned rather than hidden because the form says
 * "Suggested" for a guess and nothing at all when it has no idea, and those
 * should not look the same.
 */
export function suggestGroup(name, { partyKind = null } = {}) {
  /* An explicit party kind beats any word match. Somebody creating a supplier
     from the purchase voucher has said which it is. */
  if (partyKind === 'customer') return { groupId: GROUP_IDS.SUNDRY_DEBTORS, confidence: 'high' };
  if (partyKind === 'supplier') return { groupId: GROUP_IDS.SUNDRY_CREDITORS, confidence: 'high' };

  const text = ` ${String(name || '').toLowerCase().trim()} `;
  if (text.trim().length < 2) return { groupId: null, confidence: 'none' };

  for (const rule of SUGGESTION_RULES) {
    if (rule.words.some((word) => text.includes(word))) {
      return { groupId: rule.group, confidence: 'high' };
    }
  }

  return { groupId: null, confidence: 'none' };
}

/* ------------------------------------------------------------------ *
 * Migration from the old hardcoded heads
 *
 * lib/accounts' INCOME_CATEGORIES / EXPENSE_CATEGORIES and lib/journal's
 * JOURNAL_ONLY_CATEGORIES were the nominal heads before this module existed.
 * Every one of them maps onto a seeded ledger under a proper group, so that a
 * business with two years of history under the old model does not lose it.
 *
 * The mapping is DATA rather than code in the migration script, so that the
 * seed and the backfill cannot disagree about where "bank_charges" goes.
 * ------------------------------------------------------------------ */

export const LEGACY_CATEGORY_LEDGERS = {
  /* Expenses */
  rent: { name: 'Rent', group: GROUP_IDS.INDIRECT_EXPENSES },
  salaries: { name: 'Salaries & Wages', group: GROUP_IDS.INDIRECT_EXPENSES },
  electricity: { name: 'Electricity & Utilities', group: GROUP_IDS.INDIRECT_EXPENSES },
  transport: { name: 'Transport & Freight', group: GROUP_IDS.DIRECT_EXPENSES },
  packing: { name: 'Packing Material', group: GROUP_IDS.DIRECT_EXPENSES },
  repairs: { name: 'Repairs & Maintenance', group: GROUP_IDS.INDIRECT_EXPENSES },
  telephone: { name: 'Telephone & Internet', group: GROUP_IDS.INDIRECT_EXPENSES },
  professional: { name: 'Professional Fees', group: GROUP_IDS.INDIRECT_EXPENSES },
  bank_charges: { name: 'Bank Charges', group: GROUP_IDS.INDIRECT_EXPENSES },
  interest_paid: { name: 'Interest Paid', group: GROUP_IDS.INDIRECT_EXPENSES },
  marketing: { name: 'Marketing & Advertising', group: GROUP_IDS.INDIRECT_EXPENSES },
  insurance: { name: 'Insurance', group: GROUP_IDS.INDIRECT_EXPENSES },
  other_expense: { name: 'Other Expenses', group: GROUP_IDS.INDIRECT_EXPENSES },
  depreciation: { name: 'Depreciation', group: GROUP_IDS.INDIRECT_EXPENSES },
  bad_debts: { name: 'Bad Debts Written Off', group: GROUP_IDS.INDIRECT_EXPENSES },
  provision: { name: 'Provisions & Accruals', group: GROUP_IDS.PROVISIONS },
  rounding_adjustment: { name: 'Rounding Off', group: GROUP_IDS.INDIRECT_EXPENSES },

  /* Not costs of trading — they belong on the balance sheet. */
  drawings: { name: 'Drawings', group: GROUP_IDS.CAPITAL },
  loan_repayment: { name: 'Loan Repayment (Principal)', group: GROUP_IDS.LOANS },
  supplier_advance: { name: 'Advances to Suppliers', group: GROUP_IDS.LOANS_ADVANCES },

  /* Income */
  other_income: { name: 'Other Income', group: GROUP_IDS.INDIRECT_INCOME },
  interest_received: { name: 'Interest Received', group: GROUP_IDS.INDIRECT_INCOME },
  commission: { name: 'Commission Earned', group: GROUP_IDS.INDIRECT_INCOME },
  discount_received: { name: 'Discount Received', group: GROUP_IDS.INDIRECT_INCOME },
  scrap_sale: { name: 'Scrap / Asset Sale', group: GROUP_IDS.INDIRECT_INCOME },
  rent_received: { name: 'Rent Received', group: GROUP_IDS.INDIRECT_INCOME },

  capital_introduced: { name: 'Capital Introduced', group: GROUP_IDS.CAPITAL },
  loan_received: { name: 'Loan Received', group: GROUP_IDS.LOANS },
  customer_advance: { name: 'Advances from Customers', group: GROUP_IDS.CURRENT_LIABILITIES },

  /* The opening-balance head has no ledger of its own: opening balances are a
     property of each ledger, and a business that posted them through the old
     journal head has them re-derived by the backfill. */
  opening_balance: { name: 'Opening Balance Adjustment', group: GROUP_IDS.SUSPENSE },
};

/* ------------------------------------------------------------------ *
 * The ledgers every set of books needs on day one
 *
 * Deliberately SHORT. The specification is explicit that the chart of accounts
 * must not be pre-populated with hundreds of ledgers nobody asked for: a list
 * of two hundred heads is a list nobody reads, and the four that matter get
 * lost in it. These are the ones without which the first voucher cannot be
 * entered.
 * ------------------------------------------------------------------ */

export const SEED_LEDGERS = [
  {
    id: 'cash',
    name: 'Cash',
    groupId: GROUP_IDS.CASH,
    isSystem: true,
    description: 'The till. Every cash receipt and cash payment lands here.',
  },
  {
    id: 'sales',
    name: 'Sales',
    groupId: GROUP_IDS.SALES,
    isSystem: true,
    description: 'Revenue from invoices, at taxable value.',
  },
  {
    id: 'purchases',
    name: 'Purchases',
    groupId: GROUP_IDS.PURCHASES,
    isSystem: true,
    description: 'The cost of goods bought, at taxable value.',
  },
  {
    id: 'sales_returns',
    name: 'Sales Returns',
    groupId: GROUP_IDS.SALES,
    isSystem: true,
    description: 'Goods a customer sent back. Reduces sales.',
  },
  {
    id: 'purchase_returns',
    name: 'Purchase Returns',
    groupId: GROUP_IDS.PURCHASES,
    isSystem: true,
    description: 'Goods returned to a supplier. Reduces purchases.',
  },
  {
    id: 'output_cgst',
    name: 'Output CGST',
    groupId: GROUP_IDS.DUTIES_TAXES,
    isSystem: true,
    taxRole: 'output',
    description: 'Central GST collected on sales, owed to the government.',
  },
  {
    id: 'output_sgst',
    name: 'Output SGST',
    groupId: GROUP_IDS.DUTIES_TAXES,
    isSystem: true,
    taxRole: 'output',
    description: 'State GST collected on sales, owed to the government.',
  },
  {
    id: 'output_igst',
    name: 'Output IGST',
    groupId: GROUP_IDS.DUTIES_TAXES,
    isSystem: true,
    taxRole: 'output',
    description: 'Integrated GST collected on inter-state sales.',
  },
  {
    id: 'output_cess',
    name: 'Output Cess',
    groupId: GROUP_IDS.DUTIES_TAXES,
    isSystem: true,
    taxRole: 'output',
    description: 'Compensation cess collected on sales.',
  },
  {
    id: 'input_cgst',
    name: 'Input CGST',
    groupId: GROUP_IDS.DUTIES_TAXES,
    isSystem: true,
    taxRole: 'input',
    description: 'Central GST paid on purchases, recoverable as input credit.',
  },
  {
    id: 'input_sgst',
    name: 'Input SGST',
    groupId: GROUP_IDS.DUTIES_TAXES,
    isSystem: true,
    taxRole: 'input',
    description: 'State GST paid on purchases, recoverable as input credit.',
  },
  {
    id: 'input_igst',
    name: 'Input IGST',
    groupId: GROUP_IDS.DUTIES_TAXES,
    isSystem: true,
    taxRole: 'input',
    description: 'Integrated GST paid on inter-state purchases.',
  },
  {
    id: 'input_cess',
    name: 'Input Cess',
    groupId: GROUP_IDS.DUTIES_TAXES,
    isSystem: true,
    taxRole: 'input',
    description: 'Compensation cess paid on purchases.',
  },
  {
    id: 'round_off',
    name: 'Round Off',
    groupId: GROUP_IDS.INDIRECT_EXPENSES,
    isSystem: true,
    description: 'The paise a rounded invoice total gains or loses.',
  },
  {
    id: 'opening_stock',
    name: 'Opening Stock',
    groupId: GROUP_IDS.STOCK,
    isSystem: true,
    description: 'The value of goods on the shelf when the year started.',
  },
  {
    id: 'closing_stock',
    name: 'Closing Stock',
    groupId: GROUP_IDS.STOCK,
    isSystem: true,
    description: 'The value of goods on the shelf at the period end.',
  },
  {
    id: 'discount_allowed',
    name: 'Discount Allowed',
    groupId: GROUP_IDS.INDIRECT_EXPENSES,
    isSystem: true,
    description: 'Discount given to customers after the invoice.',
  },
  {
    id: 'other_expenses',
    name: 'Other Expenses',
    groupId: GROUP_IDS.INDIRECT_EXPENSES,
    isSystem: true,
    description: 'The catch-all. A head that keeps growing is a head that wants splitting up.',
  },
  {
    id: 'tcs_payable',
    name: 'TCS Payable',
    groupId: GROUP_IDS.DUTIES_TAXES,
    isSystem: true,
    taxRole: 'output',
    description: 'Tax collected at source on sales, owed to the government.',
  },
  {
    id: 'tds_receivable',
    name: 'TDS Receivable',
    groupId: GROUP_IDS.LOANS_ADVANCES,
    isSystem: true,
    description: 'Tax a customer deducted from your invoice. Claimed back against your own tax.',
  },
  {
    /**
     * THE OTHER SIDE OF TDS, and the one this application actually uses.
     *
     * `TDS Receivable` above is the DEDUCTEE's head: tax somebody withheld from
     * money owed to you. Every section this app implements — 194Q, 194C, 194J,
     * 194I, 194H — is a DEDUCTOR section: tax YOU withhold from a supplier and
     * owe to the government. `createVoucher` only applies TDS when we are not
     * the supplier, which says the same thing.
     *
     * Posting that to a receivable under Loans & Advances was wrong twice over
     * — an asset where a liability belongs, and debited where it had to be
     * credited — so every purchase bill carrying TDS threw twice its value into
     * Suspense and told the user the document did not reconcile.
     */
    id: 'tds_payable',
    name: 'TDS Payable',
    groupId: GROUP_IDS.DUTIES_TAXES,
    isSystem: true,
    taxRole: 'output',
    description: 'Tax you deducted from a supplier and owe to the government.',
  },
  {
    id: 'suspense',
    name: 'Suspense',
    groupId: GROUP_IDS.SUSPENSE,
    isSystem: true,
    description:
      'Where an amount waits until somebody works out what it was. Should be empty at a year end.',
  },
];


